import React, { useEffect, useState } from 'react';
import { Dimensions, Platform, ScaledSize, View } from 'react-native';
import { globalStyle } from './styles/globalStyle';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading, setPhoneMode } from './store/action';
import { loadAppState, RootState, saveToken } from './services/storage';
import UserStatusRoute from './routes/UserStatusRoute';
import { AppLoader } from './components/atomic/AppLoader';
import { OfferAppDownloadBanner } from './OfferAppDownloadBanner';
import { apiVersion } from '../configuration';
import { compareVersionStrings } from './utils/Utils';
import { UnsupportedVersionScreen } from './screens/UnsupportedVersionScreen';
import { isMobile } from './services/utils';

const windowDimensions = Dimensions.get('window');

export type MainProps = {
  route: {
    params: {
      accessToken?: string | null;
    };
  };
};

export default function Main({ route: { params } }: MainProps) {
  const [dataLoaded, setDataLoaded] = useState(false);

  const dispatch = useDispatch();

  // If were on web, we can check if an access token is provided into search params
  // And also communicate with the embeding iframe
  useEffect(() => {
    if (isMobile()) {
      return;
    }

    const listener1 = window.addEventListener('keydown', (event) => {
      window.parent.postMessage({ type: 'keydown', key: event.key }, '*');
    });

    const listener2 = window.addEventListener('mousedown', (event) => {
      window.parent.postMessage({ type: 'mousedown' }, '*');
    });

    const listener3 = window.addEventListener('mousemove', (event) => {
      window.parent.postMessage({ type: 'mousemove' }, '*');
    });

    const listener4 = window.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];

      if (touch) {
        window.parent.postMessage(
          {
            type: 'touchstart',
            clientX: touch.clientX,
            clientY: touch.clientY,
            screenX: touch.screenX,
            screenY: touch.screenY,
            pageX: touch.pageX,
            pageY: touch.pageY,
          },
          '*'
        );
      }
    });

    const listener5 = window.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];

      if (touch) {
        window.parent.postMessage(
          {
            type: 'touchend',
            clientX: touch.clientX,
            clientY: touch.clientY,
            screenX: touch.screenX,
            screenY: touch.screenY,
            pageX: touch.pageX,
            pageY: touch.pageY,
          },
          '*'
        );
      }
    });

    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('accessToken');

    if (accessToken) {
      async function runAsync() {
        dispatch(setLoading(true));
        try {
          await saveToken(accessToken as string);
          await loadAppState(dispatch);
        } catch (e) {
          console.error(e);
        } finally {
          dispatch(setLoading(false));
          setDataLoaded(true);
        }
      }

      runAsync();
    }

    return () => {
      window.removeEventListener('keydown', listener1);
      window.removeEventListener('mousedown', listener2);
      window.removeEventListener('mousemove', listener3);
      window.removeEventListener('touchstart', listener4);
      window.removeEventListener('touchend', listener5);
    };
  }, [dispatch]);

  const onChange = ({ window }: { window: ScaledSize }) => {
    dispatch(setPhoneMode(window.width < 640));
  };

  useEffect(() => {
    async function runAsync() {
      dispatch(setLoading(true));
      try {
        if (Platform.OS !== 'android' && params?.accessToken) {
          await saveToken(params.accessToken);
        }

        await loadAppState(dispatch);
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setLoading(false));
        setDataLoaded(true);
      }
    }

    runAsync();
  }, [dispatch, params?.accessToken]);

  useEffect(() => {
    dispatch(setPhoneMode(windowDimensions.width < 640));
  }, [dispatch]);

  useEffect(() => {
    const handler = Dimensions.addEventListener('change', onChange);
    return () => {
      handler.remove();
    };
  });

  const isLoading = useSelector<RootState, boolean>((state) => state.isLoading || false);
  const minimumVersion = useSelector<RootState, string>((state) => state.version.minimumVersion);

  if (!dataLoaded) {
    return null;
  }

  const shouldBeUpdated = compareVersionStrings(minimumVersion, apiVersion) > 0;

  return (
    <View style={globalStyle.screen}>
      <OfferAppDownloadBanner />
      {shouldBeUpdated ? <UnsupportedVersionScreen /> : <UserStatusRoute />}
      {isLoading && <AppLoader isVisible={isLoading} />}
    </View>
  );
}
